import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_konattu-interactive/artifacts/898w1al1_ikshana-productions-bVtcNC64Jgc-unsplash.webp";
const EASE = [0.76, 0, 0.24, 1];

const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
    <motion.span
      className={`block pb-[0.02em] ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const go = (id) => window.__lenis?.scrollTo(`#${id}`, { offset: -70 });

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] min-h-[680px] w-full overflow-hidden bg-konattu-charcoal" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Premium Konattu home interior" className="w-full h-full object-cover -scale-x-100" />
        <div className="absolute inset-0 bg-gradient-to-r from-konattu-charcoal/95 via-konattu-charcoal/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-konattu-maroon/75 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-konattu-charcoal/15" />
      </motion.div>

      <motion.div style={{ y: overlayY }} className="relative z-10 h-full max-w-[1500px] mx-auto px-6 lg:px-10 flex flex-col justify-center items-start">
        <div className="w-full md:max-w-2xl md:mr-auto text-left">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="font-mono text-[11px] md:text-xs tracking-[0.35em] text-konattu-yellow mb-6 drop-shadow-[0_1px_8px_rgba(0,0,0,0.6)]"
            data-testid="hero-eyebrow"
          >
            EST. 1998 · KERALA · YOUR QUALITY SHOPPING PARTNER
          </motion.p>

          <h1 className="font-display font-extrabold text-[#F7F2E9] tracking-tightest leading-[0.9] text-5xl sm:text-7xl lg:text-8xl drop-shadow-[0_3px_18px_rgba(0,0,0,0.55)]" data-testid="hero-heading">
            <MaskLine delay={0.15}>Your quality</MaskLine>
            <MaskLine delay={0.3} className="text-konattu-red">shopping</MaskLine>
            <MaskLine delay={0.45}>partner.</MaskLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1, ease: EASE }}
            className="mt-8 flex flex-col items-start gap-8"
          >
            <p className="max-w-md font-body text-base md:text-lg text-white/90 leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
              Adding value to life. A trusted Kerala retail group, from everyday essentials to aspirational purchases, backed by nearly three decades of local relationships.
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => go("divisions")} data-testid="hero-explore-btn" className="bg-konattu-red text-white px-8 py-4 text-sm font-medium tracking-wide hover:bg-konattu-orange transition-colors duration-300">
                Explore our divisions
              </button>
              <button onClick={() => go("network")} data-testid="hero-network-btn" className="border border-[#F7F2E9]/40 text-[#F7F2E9] px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#F7F2E9] hover:text-konattu-charcoal transition-colors duration-300">
                Store network
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-10 z-10 hidden md:flex items-center gap-2 text-[#F7F2E9]/60"
      >
        <ArrowDown size={16} className="animate-bounce" />
        <span className="font-mono text-[10px] tracking-[0.3em]">SCROLL</span>
      </motion.div>
    </section>
  );
};

export default Hero;
